import { useCallback, useEffect, useReducer, useRef } from 'react';
import { _isNil, _notNil } from '@/littledash';
import type {
  AgentSessionPhase,
  AgentSessionV1,
  AgentStreamEventType,
  AgentStreamEventV1,
  StreamingText,
} from '@/model/Agent.model';
import { TERMINAL_STREAM_EVENTS } from '@/model/Agent.model';
import { AgentSessionService } from '@/support/AgentSessionService';
import { type ApiResponse, ResponseType } from '@/support/ApiService';
import type { Endpoints } from '@/generated/internal-api/Endpoints';
import { useAbortController } from '@/support/Hooks/fetch/useAbortController';
import { useAgentStream } from '@/support/Hooks/agent/useAgentStream';

export interface SessionState {
  phase: AgentSessionPhase;
  session: AgentSessionV1 | null;
  activeExecutionId: string | null;
  streamingText: StreamingText | null;
  streamEvents: Array<AgentStreamEventV1>;
  error: string | null;
}

type SessionAction =
  | { type: 'CREATE_START' }
  | { type: 'CREATE_SUCCESS'; session: AgentSessionV1 }
  | { type: 'CREATE_ERROR'; error: string }
  | { type: 'STREAM_EVENT'; event: AgentStreamEventV1 }
  | { type: 'STREAM_TEXT_DELTA'; event: AgentStreamEventV1 }
  | { type: 'STREAM_TEXT_DONE'; event: AgentStreamEventV1 }
  | { type: 'STREAM_TERMINAL'; eventType: AgentStreamEventType }
  | { type: 'STREAM_ERROR'; error: string }
  | { type: 'REFETCH_SUCCESS'; session: AgentSessionV1 }
  | { type: 'RESET' };

const initialState: SessionState = {
  phase: 'idle',
  session: null,
  activeExecutionId: null,
  streamingText: null,
  streamEvents: [],
  error: null,
};

export const sessionReducer = (state: SessionState, action: SessionAction): SessionState => {
  switch (action.type) {
    case 'CREATE_START':
      return { ...initialState, phase: 'creating' };

    case 'CREATE_SUCCESS': {
      const firstExecution = action.session.executions[0];
      return {
        ...state,
        phase: 'streaming',
        session: action.session,
        activeExecutionId: firstExecution?.api_id ?? null,
        error: null,
      };
    }

    case 'CREATE_ERROR':
      return { ...state, phase: 'failed', error: action.error };

    case 'STREAM_EVENT':
      return { ...state, streamEvents: [...state.streamEvents, action.event] };

    case 'STREAM_TEXT_DELTA': {
      const currentText = state.streamingText?.accumulatedText ?? '';
      const content = action.event.type === 'text_delta' ? action.event.content : '';
      return {
        ...state,
        streamingText: {
          executionApiId: action.event.execution_api_id,
          accumulatedText: currentText + content,
          isStreaming: true,
        },
      };
    }

    case 'STREAM_TEXT_DONE': {
      const content =
        action.event.type === 'text_done' ? action.event.content : (state.streamingText?.accumulatedText ?? '');
      return {
        ...state,
        streamingText: {
          executionApiId: action.event.execution_api_id,
          accumulatedText: content,
          isStreaming: false,
        },
      };
    }

    case 'STREAM_TERMINAL': {
      if (state.phase !== 'streaming') {
        return state;
      }
      const phase: AgentSessionPhase =
        action.eventType === 'completed' ? 'completed' : action.eventType === 'cancelled' ? 'cancelled' : 'failed';
      return {
        ...state,
        phase,
        streamingText: _notNil(state.streamingText) ? { ...state.streamingText, isStreaming: false } : null,
      };
    }

    case 'STREAM_ERROR':
      return { ...state, phase: 'failed', error: action.error };

    case 'REFETCH_SUCCESS':
      return { ...state, session: action.session };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
};

export interface UseAgentSessionOutput {
  state: SessionState;
  createSession: (creator: (signal: AbortSignal) => Promise<ApiResponse<keyof Endpoints>>) => Promise<void>;
  cancel: () => Promise<void>;
  reset: () => void;
}

/**
 * Reducer-driven state machine that owns one agent session: kicks off the
 * session via a caller-supplied creator, subscribes to the SSE stream, tracks
 * accumulated text deltas, and refetches the durable session record when the
 * stream reaches a terminal state.
 *
 * Stream-event dedupe is handled here via a Set in a ref rather than in
 * reducer state so the Set is mutated in place — avoids O(n) Set-copy on
 * every event during long streams.
 */
export const useAgentSession = (): UseAgentSessionOutput => {
  const [state, dispatch] = useReducer(sessionReducer, initialState);
  const { newAbortController, abort } = useAbortController();
  const disconnectRef = useRef<(() => void) | null>(null);
  const seenEventIdsRef = useRef<Set<string>>(new Set());

  const onEvent = useCallback((event: AgentStreamEventV1) => {
    if (_notNil(event.id)) {
      if (seenEventIdsRef.current.has(event.id)) {
        return;
      }
      seenEventIdsRef.current.add(event.id);
    }
    if (event.type === 'text_delta') {
      dispatch({ type: 'STREAM_TEXT_DELTA', event });
    } else if (event.type === 'text_done') {
      dispatch({ type: 'STREAM_TEXT_DONE', event });
    } else if (TERMINAL_STREAM_EVENTS.has(event.type)) {
      dispatch({ type: 'STREAM_TERMINAL', eventType: event.type });
    } else {
      dispatch({ type: 'STREAM_EVENT', event });
    }
  }, []);

  const onError = useCallback((error: string) => {
    dispatch({ type: 'STREAM_ERROR', error });
  }, []);

  const onTerminal = useCallback((eventType: AgentStreamEventType) => {
    dispatch({ type: 'STREAM_TERMINAL', eventType });
  }, []);

  const { disconnect } = useAgentStream({
    sessionApiId: state.phase === 'streaming' ? (state.session?.api_id ?? null) : null,
    onEvent,
    onError,
    onTerminal,
  });

  useEffect(() => {
    disconnectRef.current = disconnect;
  });

  const sessionApiId = state.session?.api_id;
  const phase = state.phase;
  useEffect(() => {
    if (_isNil(sessionApiId)) {
      return;
    }
    if (phase !== 'completed' && phase !== 'failed' && phase !== 'cancelled') {
      return;
    }

    const controller = new AbortController();
    AgentSessionService.getSession(sessionApiId, controller.signal).then((response) => {
      if (response.type === ResponseType.Success && _notNil(response.body)) {
        const body = response.body as { data: AgentSessionV1 };
        dispatch({ type: 'REFETCH_SUCCESS', session: body.data });
      }
    });

    return () => {
      controller.abort();
    };
  }, [sessionApiId, phase]);

  const createSession = useCallback(
    async (creator: (signal: AbortSignal) => Promise<ApiResponse<keyof Endpoints>>) => {
      seenEventIdsRef.current = new Set();
      dispatch({ type: 'CREATE_START' });
      const controller = newAbortController();
      const response = await creator(controller.signal);
      if (response.type === ResponseType.Success && _notNil(response.body)) {
        const body = response.body as { data: AgentSessionV1 };
        dispatch({ type: 'CREATE_SUCCESS', session: body.data });
      } else {
        dispatch({ type: 'CREATE_ERROR', error: response.error?.message ?? 'Failed to create session' });
      }
    },
    [newAbortController]
  );

  const cancel = useCallback(async () => {
    if (_isNil(state.session)) {
      return;
    }
    disconnectRef.current?.();
    const controller = newAbortController();
    const response = await AgentSessionService.cancelSession(state.session.api_id, controller.signal);
    if (response.type === ResponseType.Success) {
      dispatch({ type: 'STREAM_TERMINAL', eventType: 'cancelled' });
    } else {
      dispatch({ type: 'STREAM_ERROR', error: response.error?.message ?? 'Failed to cancel session' });
    }
  }, [state.session, newAbortController]);

  const reset = useCallback(() => {
    abort();
    disconnectRef.current?.();
    seenEventIdsRef.current = new Set();
    dispatch({ type: 'RESET' });
  }, [abort]);

  return { state, createSession, cancel, reset };
};
